import getSubscriptionStatus from "@helpers/getSubscriptionStatus"
import { getPlanDetails } from "@helpers/plans"
import { getUserCurrency } from "@helpers/sessionStorage"
import { getSession, listStripePrices } from "@helpers/supabase"
import { defer, redirect } from "react-router-dom"
import { Plan, StripePrice } from "src/types"

export interface GetPricesLoaderResult {
  getPricesData: GetPricesData
}

export interface PlanPrices {
  full: StripePrice | undefined
  intro: StripePrice | undefined
}

export interface GetPricesData {
  currency: string
  prices: Record<Plan, PlanPrices>
  subscriptionStatus: "active" | "inactive" | "none"
}

const PLANS: Plan[] = ["monthly", "quarterly", "yearly"]

export default function getPricesLoader() {
  const getPricesPromise = getPrices()

  return defer({
    getPricesData: getPricesPromise,
  })
}

async function getPrices(): Promise<GetPricesData | Response> {
  const [{ data }, stripePrices] = await Promise.all([
    getSession(),
    listStripePrices(),
  ])

  const currency = await findCurrency({ stripePrices })

  const userId = data?.session?.user?.id

  if (!userId) {
    return redirect("/sign-up")
  }

  // NOTE: Not needed to show the paywall, so a failed RevenueCat request (seen
  // on flaky mobile networks behind PRODUCTION-9) doesn't take it down.
  const subscriptionStatus = await getSubscriptionStatus(userId).catch(
    (error: unknown) => {
      console.error(error)

      return "none" as const
    },
  )

  const findPrice = (lookupKey: string) =>
    stripePrices.find((price) => price.lookup_key === lookupKey)

  const prices = Object.fromEntries(
    PLANS.map((plan) => {
      const { fullLookupKey, introLookupKey } = getPlanDetails(plan)

      return [
        plan,
        { full: findPrice(fullLookupKey), intro: findPrice(introLookupKey) },
      ]
    }),
  ) as Record<Plan, PlanPrices>

  return { currency, prices, subscriptionStatus }
}

async function findCurrency({
  stripePrices,
}: {
  stripePrices: StripePrice[]
}): Promise<string> {
  const userCurrency = (await getUserCurrency()).toLowerCase()

  const stripePriceCurrencies = stripePrices.flatMap((price) => [
    price.currency,
    ...Object.keys(price?.currency_options),
  ])

  return stripePriceCurrencies.includes(userCurrency) ? userCurrency : "eur"
}
